<script>
import { mapActions } from 'vuex'
import { publishedAtToFormValue } from '@/support/utils/articleDate'
import { truncateCodePoints } from '@/support/utils/unicodeText'
import { MAX_SEO_TITLE, MAX_SEO_SUBTITLE } from './steps/ArticleSeoBasicStep.vue'

const STEP_CONTENT = 1
const STEP_SEO_BASIC = 2
const STEP_SEO_ADVANCED = 3
const STEP_SOCIAL = 4

const SAVE_ERROR_MESSAGES = [
  [
    'article__slug_already_exists',
    'articles.error:slug.already_exists'
  ],
  [
    'article__seo_title_too_long',
    'articles.error:seo.title.too_long'
  ],
  [
    'article__meta_description_too_long',
    'articles.error:meta.description.too_long'
  ],
  [
    'article__faq_question_required',
    'articles.error:faq.question.required'
  ],
  [
    'article__faq_answer_required',
    'articles.error:faq.answer.required'
  ],
  [
    'article__canonical_url_invalid',
    'articles.error:canonical.invalid'
  ],
  [
    'article__canonical_url_too_long',
    'articles.error:canonical.too_long'
  ],
  [
    'article__link_rel_url_required',
    'articles.error:linkrel.url.required'
  ],
  [
    'article__link_rel_url_too_long',
    'articles.error:linkrel.url.too_long'
  ],
  [
    'article__link_rel_url_invalid',
    'articles.error:linkrel.url.invalid'
  ],
  [
    'article__redirect_old_url_required',
    'articles.error:redirect.old_url.required'
  ],
  [
    'article__redirect_old_url_too_long',
    'articles.error:redirect.old_url.too_long'
  ],
  [
    'article__redirect_old_url_duplicated',
    'articles.error:redirect.old_url.duplicated'
  ],
  [
    'article__redirect_old_url_in_use',
    'articles.error:redirect.old_url.in_use'
  ],
  [
    'article__redirect_old_url_is_destination',
    'articles.error:redirect.old_url.is_destination'
  ],
  [
    'article__redirect_loop',
    'articles.error:redirect.loop'
  ],
  [
    'article__redirect_old_url_invalid',
    'articles.error:redirect.old_url.invalid'
  ],
  [
    'article__published_at_not_allowed',
    'articles.error:published_at.forbidden'
  ],
  [
    'article__published_at_invalid',
    'articles.error:published_at.invalid'
  ]
]

const emptyFormData = () => ({
  id: null,
  title: '',
  subtitle: '',
  slug: '',
  id_theme: null,
  author: '',
  reading_time: null,
  publisher: 'Sebrae SP',
  summary: '',
  body: '',
  cover_image: null,
  cover_image_title: '',
  cover_image_caption: '',
  cover_image_alt: '',
  card_image: null,
  card_image_title: '',
  card_image_caption: '',
  card_image_alt: '',
  status: 'draft',
  featured: false,
  publishedAt: null,
  createdAt: null,
  updatedAt: null,
  userUpdatedAt: null,
  seo_keyword: '',
  seo_title: '',
  seo_subtitle: '',
  meta_description: '',
  faqs: [],
  canonical_url: '',
  meta_noindex: false,
  meta_nofollow: false,
  meta_noarchive: false,
  meta_nosnippet: false,
  meta_notranslate: false,
  meta_noimageindex: false,
  link_rels: [],
  redirects: [],
  breadcrumbs: [],
  og_image: null,
  og_image_resolved: null,
  recommendations_enabled: false,
  recommendations: []
})

export default {
  name: 'ArticlesManage',
  components: {
    Action: () => import('@/components/general/Action'),
    ActionBar: () => import('@/components/general/ActionBar'),
    ContentHeader: () => import('@/components/general/ContentHeader'),
    Pagination: () => import('@/components/general/Pagination'),
    ArticleContentStep: () => import('./steps/ArticleContentStep'),
    ArticleSeoBasicStep: () => import('./steps/ArticleSeoBasicStep'),
    ArticleSeoAdvancedStep: () => import('./steps/ArticleSeoAdvancedStep'),
    ArticleSocialStep: () => import('./steps/ArticleSocialStep')
  },

  data () {
    return {
      currentStep: STEP_CONTENT,
      maxReachedStep: STEP_CONTENT,
      formData: emptyFormData()
    }
  },

  computed: {
    isEditing () {
      return Boolean(this.$route.params.id)
    },

    canEditPublishedAt () {
      return this.getContextPermission('articles') === 'write'
    },

    themes () {
      return this.$store.getters.articleThemesList
    },

    headerTitle () {
      return this.isEditing && this.formData.title
        ? this.formData.title
        : this.$t('articles.manage:header.title.new')
    },

    breadcrumbLabels () {
      const labels = [
        'Home',
        'Blog'
      ]

      labels.push(
        this.formData.title || this.$t('articles.form:breadcrumbs.placeholder.title')
      )

      return labels
    }
  },

  mounted () {
    if (this.$route.params.isNew === 'new') {
      this.currentStep = STEP_SEO_BASIC
    } else if (this.$route.params.isNew === 'edit') {
      this.currentStep = Number(this.$route.params.step) || this.currentStep
    }
  },

  created () {
    this.attemptGetArticleThemesList()

    if (this.isEditing) {
      this.loadArticle(this.$route.params.id)
    }
  },

  methods: {
    ...mapActions([
      'setFetching',
      'setFeedback',
      'attemptGetArticle',
      'attemptCreateArticle',
      'attemptUpdateArticle',
      'attemptGetArticleThemesList'
    ]),

    async loadArticle (id) {
      this.setFetching(true)

      try {
        const article = await this.attemptGetArticle(id)

        this.formData = {
          ...emptyFormData(),
          id: article.id,
          title: article.title || '',
          subtitle: article.subtitle || '',
          slug: article.slug || '',
          id_theme: article.theme ? article.theme.id : null,
          author: article.author || '',
          reading_time: article.readingTime || null,
          publisher: article.publisher || 'Sebrae SP',
          summary: article.summary || '',
          body: article.body || '',
          cover_image: article.coverImage || null,
          cover_image_title: article.coverImageTitle || '',
          cover_image_caption: article.coverImageCaption || '',
          cover_image_alt: article.coverImageAlt || '',
          card_image: article.cardImage || null,
          card_image_title: article.cardImageTitle || '',
          card_image_caption: article.cardImageCaption || '',
          card_image_alt: article.cardImageAlt || '',
          status: article.status || 'draft',
          featured: Boolean(article.featured),
          publishedAt: publishedAtToFormValue(article.publishedAt),
          createdAt: article.createdAt || null,
          updatedAt: article.updatedAt || null,
          userUpdatedAt: article.userUpdatedAt || null,
          seo_keyword: article.seoKeyword || '',
          seo_title: article.seoTitle || '',
          seo_subtitle: article.seoSubtitle || '',
          meta_description: article.metaDescription || '',
          faqs: Array.isArray(article.faqs)
            ? article.faqs.map(f => ({
              id: f.id || null,
              question: f.question || '',
              answer: f.answer || ''
            }))
            : [],

          canonical_url: article.canonicalUrl || '',
          meta_noindex: Boolean(article.metaNoindex),
          meta_nofollow: Boolean(article.metaNofollow),
          meta_noarchive: Boolean(article.metaNoarchive),
          meta_nosnippet: Boolean(article.metaNosnippet),
          meta_notranslate: Boolean(article.metaNotranslate),
          meta_noimageindex: Boolean(article.metaNoimageindex),
          link_rels: Array.isArray(article.linkRels)
            ? article.linkRels.map(r => ({
              id: r.id || null,
              url: r.url || '',
              nofollow: Boolean(r.nofollow),
              sponsored: Boolean(r.sponsored)
            }))
            : [],

          redirects: Array.isArray(article.redirects)
            ? article.redirects.map(r => ({
              id: r.id || null,
              old_url: r.oldUrl || '',
              new_url: r.newUrl || ''
            }))
            : [],

          breadcrumbs: Array.isArray(article.breadcrumbs) ? article.breadcrumbs : [],

          og_image: article.ogImage || null,
          og_image_resolved: article.ogImageResolved || null,
          recommendations_enabled: Boolean(article.recommendationsEnabled),
          recommendations: Array.isArray(article.recommendations)
            ? article.recommendations
            : []
        }

        this.maxReachedStep = STEP_SOCIAL
      } catch (err) {
        this.setFeedback({ message: this.$t('articles.feedback:load.error') })
      } finally {
        this.setFetching(false)
      }
    },

    onFormDataInput (next) {
      this.formData = {
        ...this.formData,
        ...next
      }
    },

    currentStepRef () {
      const refMap = {
        [STEP_CONTENT]: 'stepContent',
        [STEP_SEO_BASIC]: 'stepSeoBasic',
        [STEP_SEO_ADVANCED]: 'stepSeoAdvanced',
        [STEP_SOCIAL]: 'stepSocial'
      }

      return this.$refs[refMap[this.currentStep]]
    },

    fillSeoData () {
      if (this.currentStep === STEP_SEO_BASIC) {
        if (!this.formData.seo_title) {
          this.formData.seo_title = truncateCodePoints(this.formData.title || '', MAX_SEO_TITLE)
        }

        if (!this.formData.seo_subtitle) {
          this.formData.seo_subtitle = truncateCodePoints(this.formData.subtitle || '', MAX_SEO_SUBTITLE)
        }
      }
    },

    prevStep () {
      this.submit()
        .then(() => {
          if (this.currentStep > STEP_CONTENT) this.currentStep -= 1
        })
    },

    nextStep () {
      const stepRef = this.currentStepRef()

      if (stepRef && typeof stepRef.validate === 'function' && !stepRef.validate()) {
        this.setFeedback({ message: this.$t('global:feedback.validation.error') })

        return
      }

      this.setFetching(true)
      this.fillSeoData()

      if (this.formData.id) {
        const payload = this.buildPayload()

        this.attemptUpdateArticle({
          idArticle: this.formData.id,
          payload
        })
          .then(() => {
            this.currentStep += 1
            this.maxReachedStep = Math.max(this.maxReachedStep, this.currentStep)
            const path = `/articles/manage/${this.formData.id}`

            if (this.$route.path !== path) {
              this.$router.push({
                name: 'articles.manage',
                params: {
                  id: this.formData.id,
                  isNew: 'edit'
                }
              })
            }
          })
          .catch((err) => {
            this.setFeedback({ message: this.resolveSaveError(err) })
          })
          .finally(() => {
            this.setFetching(false)
          })
      } else {
        const payload = this.buildPayload()

        this.attemptCreateArticle(payload)
          .then((saved) => {
            this.formData.id = saved.id
            if (saved.slug) this.formData.slug = saved.slug

            this.$router.push({
              name: 'articles.manage',
              params: {
                id: saved.id,
                isNew: 'new'
              }
            })

            this.setFeedback({ message: this.$t('articles.feedback:save.draft.success') })
          })
          .catch((err) => {
            this.setFeedback({ message: this.resolveSaveError(err) })
          })
          .finally(() => {
            this.setFetching(false)
          })
      }
    },

    submit () {
      this.setFetching(true)
      const payload = this.buildPayload()

      if (this.formData.id) {
        return this.attemptUpdateArticle({
          idArticle: this.formData.id,
          payload
        })
          .finally(() => {
            this.setFetching(false)
          })
      }

      return this.attemptCreateArticle(payload)
        .then((saved) => {
          this.formData.id = saved.id
          if (saved.slug) this.formData.slug = saved.slug
        })
        .finally(() => {
          this.setFetching(false)
        })
    },

    buildPayload () {
      const f = this.formData
      const payload = {
        title: f.title,
        subtitle: f.subtitle,
        slug: f.slug,
        id_theme: f.id_theme,
        author: f.author,
        reading_time: f.reading_time,
        publisher: f.publisher,
        summary: f.summary,
        body: f.body,
        cover_image: f.cover_image && typeof f.cover_image === 'object' ? f.cover_image : null,
        cover_image_title: f.cover_image_title,
        cover_image_caption: f.cover_image_caption,
        cover_image_alt: f.cover_image_alt,
        card_image: f.card_image && typeof f.card_image === 'object' ? f.card_image : null,
        card_image_title: f.card_image_title,
        card_image_caption: f.card_image_caption,
        card_image_alt: f.card_image_alt,
        seo_keyword: f.seo_keyword,
        seo_title: f.seo_title,
        seo_subtitle: f.seo_subtitle,
        meta_description: f.meta_description,
        faqs: Array.isArray(f.faqs)
          ? f.faqs.map(({ id, question, answer }) => ({
            id,
            question,
            answer
          }))
          : [],

        canonical_url: f.canonical_url,
        meta_noindex: f.meta_noindex,
        meta_nofollow: f.meta_nofollow,
        meta_noarchive: f.meta_noarchive,
        meta_nosnippet: f.meta_nosnippet,
        meta_notranslate: f.meta_notranslate,
        meta_noimageindex: f.meta_noimageindex,
        link_rels: Array.isArray(f.link_rels)
          ? f.link_rels.map(({ id, url, nofollow, sponsored }) => ({
            id,
            url,
            nofollow,
            sponsored
          }))
          : [],

        redirects: Array.isArray(f.redirects)
          ? f.redirects.map(r => {
            const redirect = { old_url: r.old_url }

            if (r.id) redirect.id = r.id

            return redirect
          })
          : [],

        og_image: f.og_image && typeof f.og_image === 'object' ? f.og_image : (f.og_image === null ? '' : undefined),
        recommendations_enabled: f.recommendations_enabled,
        recommendations: Array.isArray(f.recommendations)
          ? f.recommendations.map(r => ({ id_recommended_article: r.recommendedId }))
          : []
      }

      if (this.canEditPublishedAt) {
        payload.published_at = f.publishedAt || null
      }

      return payload
    },

    resolveSaveErrorMessage (msg) {
      const match = SAVE_ERROR_MESSAGES.find(([ code ]) => msg.includes(code))

      return this.$t(match ? match[1] : 'articles.feedback:save.draft.error')
    },

    resolveSaveError (err) {
      const msg = err?.data?.error?.message || err?.data?.message || ''

      if (err?.status === 403 && msg !== 'access_not_allowed') {
        return this.$t('articles.error:published_at.forbidden')
      }

      return this.resolveSaveErrorMessage(msg)
    },

    async saveDraft () {
      const stepRef = this.currentStepRef()

      if (stepRef && typeof stepRef.validate === 'function' && !stepRef.validate()) {
        this.setFeedback({ message: this.$t('global:feedback.validation.error') })

        return
      }

      this.setFetching(true)

      try {
        const payload = this.buildPayload()
        let saved

        if (this.formData.id) {
          saved = await this.attemptUpdateArticle({
            idArticle: this.formData.id,
            payload
          })
        } else {
          saved = await this.attemptCreateArticle(payload)
        }

        this.formData.id = saved.id
        if (saved.slug) this.formData.slug = saved.slug
        if (saved.publishedAt) this.formData.publishedAt = publishedAtToFormValue(saved.publishedAt)
        if (saved.updatedAt) this.formData.updatedAt = saved.updatedAt
        if (saved.userUpdatedAt) this.formData.userUpdatedAt = saved.userUpdatedAt
        if (saved.coverImage) this.formData.cover_image = saved.coverImage
        if (saved.cardImage) this.formData.card_image = saved.cardImage

        this.setFeedback({ message: this.$t('articles.feedback:save.draft.success') })
        this.$router.push({ name: 'articles.index' })
      } catch (err) {
        this.setFeedback({ message: this.resolveSaveError(err) })
      } finally {
        this.setFetching(false)
      }
    },

    backToList () {
      this.$router.push({ name: 'articles.index' })
    }
  }
}
</script>

<template>
  <div class="main-content articles-manage">
    <ContentHeader
      :title="headerTitle"
      dark-theme
    >
      <Action
        slot="back"
        type="button"
        :text="$t('global:back')"
        class="btn-back"
        icon="keyboard_backspace"
        @click="backToList"
      />
      <ActionBar slot="actionbar" />
      <template slot="buttons">
        <Action
          :text="$t('global:form.save')"
          type="button"
          dark
          flat
          @click="saveDraft"
        />
      </template>
    </ContentHeader>

    <div
      v-show="currentStep === 1"
      class="main-content-inner"
    >
      <div class="center">
        <div class="solutions-create-header">
          <h2 class="solutions-create-title">
            {{ $t('global:form.step', {'num': 1}) }}
          </h2>
          <p class="solutions-create-description">
            {{ $t('articles.manage:step.content') }}
          </p>
        </div>
        <ArticleContentStep
          ref="stepContent"
          :form-data="formData"
          :themes="themes"
          :is-editing="isEditing"
          :can-edit-published-at="canEditPublishedAt"
          @input="onFormDataInput"
        />
      </div>
    </div>
    <div
      v-show="currentStep === 2"
      class="main-content-inner"
    >
      <div class="center">
        <div class="solutions-create-header">
          <h2 class="solutions-create-title">
            {{ $t('global:form.step', {'num': 2}) }}
          </h2>
          <p class="solutions-create-description">
            {{ $t('articles.manage:step.seo.basic') }}
          </p>
        </div>
        <ArticleSeoBasicStep
          ref="stepSeoBasic"
          :form-data="formData"
          :is-editing="isEditing"
          @input="onFormDataInput"
        />
      </div>
    </div>
    <div
      v-show="currentStep === 3"
      class="main-content-inner"
    >
      <div class="center">
        <div class="solutions-create-header">
          <h2 class="solutions-create-title">
            {{ $t('global:form.step', {'num': 3}) }}
          </h2>
          <p class="solutions-create-description">
            {{ $t('articles.manage:step.seo.advanced') }}
          </p>
        </div>
        <ArticleSeoAdvancedStep
          ref="stepSeoAdvanced"
          :form-data="formData"
          :breadcrumb-labels="breadcrumbLabels"
          @input="onFormDataInput"
        />
      </div>
    </div>
    <div
      v-show="currentStep === 4"
      class="main-content-inner"
    >
      <div class="center">
        <div class="solutions-create-header">
          <h2 class="solutions-create-title">
            {{ $t('global:form.step', {'num': 4}) }}
          </h2>
          <p class="solutions-create-description">
            {{ $t('articles.manage:step.social') }}
          </p>
        </div>
        <ArticleSocialStep
          ref="stepSocial"
          :form-data="formData"
          :is-editing="isEditing"
          @input="onFormDataInput"
        />
      </div>
    </div>
    <Pagination
      class="center"
      :active-first-last="false"
      :active-page="currentStep"
      :page-count="4"
      @nextPage="nextStep"
      @previousPage="prevStep"
    />
  </div>
</template>

<style src="@/assets/styles/themes/default/solutions.css"></style>

<style src="@/assets/styles/themes/default/articles.css"></style>
